import { useState, useEffect } from "react";
import { TextField, Button, Divider, Chip, Autocomplete, CircularProgress, Box, Typography, Card, CardContent, Alert } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import BuildIcon from "@mui/icons-material/Build";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import PaletteIcon from "@mui/icons-material/Palette";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SpeedIcon from "@mui/icons-material/Speed";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import NotesIcon from "@mui/icons-material/Notes";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { motion } from "framer-motion";
import mainStyles from "../Main.module.css";
import loadingImage from "../loading.gif";
import validator from "validator";
import api from "../../services/api";
import { useMyContext } from "../../store/ContextApi";
import { useDispatch, useSelector } from "react-redux";
import { getColors } from "../../State/Color/Action";
import { getMyWorkshops } from "../../State/Workshop/Action";
import { getBrands } from "../../State/Brand/Action";
import { getModelsByBrand } from "../../State/Model/Action";
import { registerUsedBike } from "../../State/UsedBike/Action";

export default function UsedBike() {
  const colors = useSelector((store) => store.colorResponse.colors);
  const brands = useSelector((store) => store.brandResponse.brands);
  const models = useSelector((store) => store.modelResponse.models);
  const workshops = useSelector(
    (store) => store.workshopResponse.workshopPaginated.workshops
  );
  const bike = useSelector((store) => store.usedBikesResponse.bike);

  const [bikeId, setBikeId] = useState("");
  const [brandId, setBrandId] = useState();
  const [bikeModelId, setBikeModelId] = useState();
  const [color, setColor] = useState();
  const [purchaseYear, setPurchaseYear] = useState();
  const [runningKms, setRunningKms] = useState();
  const [sellerAddress, setSellerAddress] = useState();
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [price, setPrice] = useState();
  const [registrationNumber, setRegistrationNumber] = useState();
  const [description, setDescription] = useState();
  const [workshopId, setWorkshopId] = useState();

  const [imageStates, setImageStates] = useState({
    FRONT: {
      file: null,
      url: "",
      loading: false,
      uploaded: false,
      error: false,
    },
    TOP: { file: null, url: "", loading: false, uploaded: false, error: false },
    LEFT: {
      file: null,
      url: "",
      loading: false,
      uploaded: false,
      error: false,
    },
    RIGHT: {
      file: null,
      url: "",
      loading: false,
      uploaded: false,
      error: false,
    },
    BACK: {
      file: null,
      url: "",
      loading: false,
      uploaded: false,
      error: false,
    },
  });

  const [isSaved, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const { isDealer, isAdmin } = useMyContext();
  const isCustomer = !isDealer && !isAdmin;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getColors());
    dispatch(getBrands());
    dispatch(getMyWorkshops(0));
    // Reset saved state on component mount
    setIsSaved(false);
    setErrorMessage(null);
  }, []);


  useEffect(() => {
    if (bike && isLoading) {
      // Only show success if we were actually submitting
      onSuccessfulSave();
      resetForm();
    }
  }, [bike, isLoading]);

  const loadModels = (e, brand) => {
    setBrandId(brand);
    setSelectedModel(null);
    setBikeModelId(null);
    if (brand) {
      dispatch(getModelsByBrand(brand));
    }
  };

  const handleImageChange = (angle, file) => {
    if (!file) return;

    const newImageStates = { ...imageStates };
    newImageStates[angle].loading = true;
    setImageStates(newImageStates);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("imageAngle", angle);
    formData.append("bikeId", bikeId);

    api
      .post("/usedBikes/images", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const updated = { ...imageStates };
        updated[angle] = {
          file: file,
          url: res.data.imageUrl,
          loading: false,
          uploaded: true,
          error: false,
        };
        setImageStates(updated);
        setBikeId(res.data.bikeId);
      })
      .catch(() => {
        const updated = { ...imageStates };
        updated[angle].loading = false;
        updated[angle].error = true;
        setImageStates(updated);
      });
  };


  const isValidForm = () => {
    setErrorMessage();
    if (!bikeId) {
      setErrorMessage("Bike Front image is required");
      return false;
    } else if (!brandId) {
      setErrorMessage("Brand is required");
      return false;
    } else if (!bikeModelId) {
      setErrorMessage("Bike Model is required");
      return false;
    } else if (!color) {
      setErrorMessage("Color is required");
      return false;
    } else if (isCustomer && !sellerAddress) {
      setErrorMessage("Seller Address is required");
      return false;
    } else if (!price) {
      setErrorMessage("Price is required");
      return false;
    } else if (!imageStates.FRONT.uploaded) {
      setErrorMessage("Front View Image is required");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const customerId = localStorage.getItem("userId");
    const usedBike = {
      customerId,
      brandId,
      bikeModelId,
      color,
      description,
      purchaseYear,
      runningKms,
      ...(isCustomer && { sellerAddress }),
      price,
      registrationNumber,
      bikeId,
      workshopId,
    };

    if (isValidForm()) {
      dispatch(registerUsedBike(usedBike));
    } else {
      setIsLoading(false);
    }
  };

  const onSuccessfulSave = () => {
    setIsSaved(true);
    setIsLoading(false);
  };

  const resetForm = () => {
    setBikeId("");
    setBrandId();
    setBikeModelId();
    setColor();
    setPurchaseYear();
    setRunningKms();
    setSellerAddress();
    setSelectedWorkshop(null);
    setSelectedBrand(null);
    setSelectedModel(null);
    setSelectedColor(null);
    setSelectedYear(null);
    setPrice();
    setRegistrationNumber();
    setDescription();
    // Don't reset isSaved here - let it show the success message
    // setIsSaved(false);
    setErrorMessage(null);
    setImageStates({
      FRONT: {
        file: null,
        url: "",
        loading: false,
        uploaded: false,
        error: false,
      },
      TOP: {
        file: null,
        url: "",
        loading: false,
        uploaded: false,
        error: false,
      },
      LEFT: {
        file: null,
        url: "",
        loading: false,
        uploaded: false,
        error: false,
      },
      RIGHT: {
        file: null,
        url: "",
        loading: false,
        uploaded: false,
        error: false,
      },
      BACK: {
        file: null,
        url: "",
        loading: false,
        uploaded: false,
        error: false,
      },
    });
  };

  const years = Array.from({ length: 2025 - 2000 }, (_, i) => 2000 + i);

  return isLoading ? (
    <div className="min-h-screen flex items-center justify-center gradient-bg">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <CircularProgress size={60} sx={{ color: "primary.main" }} />
        <Typography variant="h6" className="mt-4 text-gray-600">
          Processing your bike registration...
        </Typography>
      </motion.div>
    </div>
  ) : (
    <div className="min-h-screen gradient-bg pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-8 shadow-xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-center gap-4 mb-2">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg"
            >
              <TwoWheelerIcon sx={{ fontSize: 32, color: "white" }} />
            </motion.div>
            <Typography 
              variant="h4" 
              className="font-bold"
              sx={{ 
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.025em"
              }}
            >
              Sell Your Bike
            </Typography>
          </div>
          <Typography 
            variant="body1" 
            className="text-center text-white/90"
            sx={{ fontSize: "1.1rem" }}
          >
            Register your used bike and reach thousands of buyers
          </Typography>
        </div>
      </motion.div>

      <div className="container mx-auto px-4 mt-8 space-y-6">
        {/* Seller Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <Card 
            sx={{ 
              borderRadius: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "none"
            }}
          >
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4">
              <Typography variant="h6" className="text-white font-bold flex items-center gap-2">
                <PersonIcon />
                Seller Information
              </Typography>
            </div>
            <CardContent className="p-6 space-y-5">
              {isDealer && (
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Workshop
                  </label>
                  <Autocomplete
              fullWidth
              options={workshops || []}
              value={selectedWorkshop}
              onChange={(event, newValue) => {
                setSelectedWorkshop(newValue);
                setWorkshopId(newValue?.workshopId || "");
              }}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.workshopId === value.workshopId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Workshop"
                  placeholder="Search or select workshop"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <BuildIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "primary.100",
                    },
                  }}
                >
                  <BuildIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                </Box>
              )}
              sx={{
                "& .MuiAutocomplete-popper": {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              }}
                  />
                </div>
              )}

              {isCustomer && (
                <TextField
                  fullWidth
                  onChange={(e) => setSellerAddress(e.target.value)}
                  label="Address"
                  multiline
                  rows={3}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <HomeIcon sx={{ color: "action.active", mr: 1, ml: 0.5, alignSelf: "flex-start", mt: 1.5 }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Bike Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Card 
            sx={{ 
              borderRadius: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "none"
            }}
          >
            <div className="bg-gradient-to-r from-accent-500 to-accent-600 p-4">
              <Typography variant="h6" className="text-white font-bold flex items-center gap-2">
                <TwoWheelerIcon />
                Bike Details
              </Typography>
            </div>
            <CardContent className="p-6 space-y-5">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Brand
                  </label>
            <Autocomplete
              fullWidth
              options={brands || []}
              value={selectedBrand}
              onChange={(event, newValue) => {
                setSelectedBrand(newValue);
                loadModels(event, newValue?.brandId);
              }}
              getOptionLabel={(option) => option.brandName || ""}
              isOptionEqualToValue={(option, value) => option.brandId === value.brandId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Brand"
                  placeholder="Search or select brand"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <TwoWheelerIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "primary.100",
                    },
                  }}
                >
                  <TwoWheelerIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.brandName}
                  </Typography>
                </Box>
              )}
              sx={{
                "& .MuiAutocomplete-popper": {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              }}
            />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Model
                  </label>
            <Autocomplete
              fullWidth
              options={models || []}
              value={selectedModel}
              onChange={(event, newValue) => {
                setSelectedModel(newValue);
                setBikeModelId(newValue?.modelId || "");
              }}
              disabled={!brandId}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.modelId === value.modelId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Model"
                  placeholder={brandId ? "Search or select model" : "Select brand first"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <TwoWheelerIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "primary.100",
                    },
                  }}
                >
                  <TwoWheelerIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                </Box>
              )}
              sx={{
                "& .MuiAutocomplete-popper": {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              }}
            />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Select Color
                  </label>
            <Autocomplete
              fullWidth
              options={colors || []}
              value={selectedColor}
              onChange={(event, newValue) => {
                setSelectedColor(newValue);
                setColor(newValue || "");
              }}
              getOptionLabel={(option) => option || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Color"
                  placeholder="Search or select color"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PaletteIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "primary.100",
                    },
                  }}
                >
                  <PaletteIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option}
                  </Typography>
                </Box>
              )}
              sx={{
                "& .MuiAutocomplete-popper": {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              }}
            />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <TextField
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  label="Bike Registration Number"
                  InputProps={{
                    startAdornment: (
                      <ConfirmationNumberIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={(e) => setRunningKms(e.target.value)}
                  label="Running KMs"
                  InputProps={{
                    startAdornment: (
                      <SpeedIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={(e) => setPrice(e.target.value)}
                  label="Selling Price (₹)"
                  InputProps={{
                    startAdornment: (
                      <AttachMoneyIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                      },
                    },
                  }}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3 text-lg">
                  Select Purchase Year
                </label>
                <Autocomplete
                  fullWidth
                  options={years}
                  value={selectedYear}
                  onChange={(event, newValue) => {
                    setSelectedYear(newValue);
                    setPurchaseYear(newValue || "");
                  }}
                  getOptionLabel={(option) => option.toString()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Purchase Year"
                      placeholder="Search or select year"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <CalendarTodayIcon sx={{ color: "action.active", mr: 1, ml: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "primary.50",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "primary.100",
                        },
                      }}
                    >
                      <CalendarTodayIcon sx={{ color: "primary.main", fontSize: 20 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  sx={{
                    "& .MuiAutocomplete-popper": {
                      "& .MuiPaper-root": {
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        border: "1px solid",
                        borderColor: "divider",
                      },
                    },
                  }}
                />
              </div>

              <TextField
                fullWidth
                onChange={(e) => setDescription(e.target.value)}
                label="Additional Remarks"
                multiline
                rows={3}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <NotesIcon sx={{ color: "action.active", mr: 1, ml: 0.5, alignSelf: "flex-start", mt: 1.5 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Image Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Card 
            sx={{ 
              borderRadius: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "none"
            }}
          >
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4">
              <Typography variant="h6" className="text-white font-bold flex items-center gap-2">
                <TwoWheelerIcon />
                Bike Images
              </Typography>
              <Typography variant="body2" className="text-white/90 mt-1">
                Upload images from different angles (Front image is required)
              </Typography>
            </div>
            <CardContent className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {["FRONT", "TOP", "LEFT", "RIGHT", "BACK"].map((angle, index) => {
                  const imageState = imageStates[angle];
                  const isRequired = angle === "FRONT";
                  return (
                    <motion.div
                      key={angle}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * index, duration: 0.3 }}
                      className={`border-2 rounded-2xl p-5 transition-all duration-300 ${
                        imageState.uploaded
                          ? "border-green-300 bg-green-50"
                          : isRequired
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                      } hover:shadow-lg`}
                    >
                      <div className="text-center mb-4">
                        <Chip
                          label={`${angle} View${isRequired ? " *" : ""}`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            bgcolor: isRequired ? "error.100" : "primary.100",
                            color: isRequired ? "error.700" : "primary.700",
                          }}
                        />
                      </div>

                      <div className="flex flex-col items-center space-y-4">
                        {imageState.url ? (
                          <motion.div
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            className="relative"
                          >
                            <img
                              src={imageState.url}
                              alt={angle}
                              className="rounded-xl border-2 border-gray-200 shadow-md object-cover w-full h-48"
                            />
                            {imageState.uploaded && (
                              <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                                <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                              </div>
                            )}
                          </motion.div>
                        ) : (
                          <div className="w-full h-48 flex flex-col items-center justify-center border-2 border-dashed rounded-xl bg-white hover:bg-gray-50 transition-colors">
                            <TwoWheelerIcon sx={{ fontSize: 48, color: "gray.400", mb: 1 }} />
                            <Typography variant="body2" className="text-gray-400">
                              No Image
                            </Typography>
                          </div>
                        )}

                        {imageState.loading && (
                          <Box className="flex items-center gap-2">
                            <CircularProgress size={20} />
                            <Typography variant="body2" className="text-gray-600">
                              Uploading...
                            </Typography>
                          </Box>
                        )}

                        <Button
                          variant={imageState.uploaded ? "outlined" : "contained"}
                          component="label"
                          startIcon={imageState.uploaded ? null : <SaveIcon />}
                          fullWidth
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.5,
                            ...(imageState.uploaded
                              ? {
                                  borderColor: "green.500",
                                  color: "green.600",
                                  "&:hover": {
                                    borderColor: "green.600",
                                    bgcolor: "green.50",
                                  },
                                }
                              : {
                                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                                  boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.39)",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                                    boxShadow: "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
                                    transform: "translateY(-1px)",
                                  },
                                }),
                            transition: "all 0.3s ease",
                          }}
                        >
                          {imageState.uploaded ? "Replace Image" : "Upload Image"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) =>
                              handleImageChange(angle, e.target.files[0])
                            }
                          />
                        </Button>

                        {imageState.error && (
                          <Alert 
                            severity="error" 
                            icon={<ErrorOutlineIcon />}
                            sx={{ borderRadius: "12px", width: "100%" }}
                          >
                            Upload failed. Please try again.
                          </Alert>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="flex flex-col items-center gap-4"
        >
          <Button
            onClick={handleSubmit}
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: "16px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "1.1rem",
              px: 6,
              py: 2,
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              boxShadow: "0 8px 24px rgba(37, 99, 235, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                boxShadow: "0 12px 32px rgba(37, 99, 235, 0.5)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Submit Bike Listing
          </Button>

          {(isSaved || errorMessage) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl"
            >
              {isSaved && (
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{ 
                    borderRadius: "16px",
                    fontSize: "1rem",
                    fontWeight: 500
                  }}
                >
                  Bike saved successfully! Your listing is now live.
                </Alert>
              )}
              {errorMessage && (
                <Alert 
                  severity="error" 
                  icon={<ErrorOutlineIcon />}
                  sx={{ 
                    borderRadius: "16px",
                    fontSize: "1rem",
                    fontWeight: 500
                  }}
                >
                  {errorMessage}
                </Alert>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
